import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subscription, Observable, filter, BehaviorSubject, of, combineLatest } from 'rxjs';
import moment from 'moment-timezone';
import { chartColors } from '../../../../Common/Charts/enums/chart-colors.enum';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { MentionSavingUsageDashboardComponent } from '../MentionSavingUsageDashboard/mention-saving-usage-dashboard.component';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { ProgressChartModel, ProgressChartOptionsModel } from '../../../../Common/Charts/models/progressChart/progress-chart.model';
import { MentionSavingUsageDashboardService } from '../../services/mention-saving-usage-dashboard.service';
import { MentionSavingQuota } from '../../../../api/monitoring/model/monitoring-account-configuration-api.model';
import { ChartStylesModel } from '../../../../Common/Charts/models/chart-styles.model';
import { abbreviateNumber } from '../../../../Common/Misc/helpers/formatting/number.helper';
import { TopicsService } from '../../../Core/services/topics.service';
import { NeoFacetTermFieldApiEnum } from '../../../../api/mentions/enum/neo-facet-term-field-api.enum';


@Component({
    selector: 'agpr-mention-volume-estimate-dialog',
    templateUrl: './mention-volume-estimate-dialog.component.html',
    styleUrls: ['./mention-volume-estimate-dialog.component.scss'],
    standalone: false
})
export class MentionVolumeEstimateDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    id: string = 'mentionVolumeEstimateDialog';

    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverities = messageSeverityEnum;
    readonly SAMPLE_RANGE: number = 7; // Number of days used as the sample range for usage estimation
    readonly RANGE_PERCENTAGE: number = 0.05;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    subscriptions: Subscription[] = [];
    hideSlideOverlay: boolean = false;
    loading: boolean = true;

    hasQuotaEstimate: boolean = false;

    quotaInfo: MentionSavingQuota;
    topicUsage: number = 0;
    overallUsage: number = 0;

    lowerBound: number = 0;
    upperBound: number = 0;

    estimateChart: ProgressChartModel;

    constructor(
        public coreDialogService: CoreDialogService,
        public localizationService: LocalizationService,
        private monitorConfigService: MonitorConfigService,
        private topicsService: TopicsService,
        private mentionSavingUsageDashboardService: MentionSavingUsageDashboardService,
    ) {
        super(coreDialogService, 'MentionVolumeEstimateDialog');
        this.monitorConfigService.refreshMonitoringConfigModel();
    }

    ngOnInit(): void {
        this.subscriptions.push(
            combineLatest([
                this.monitorConfigService.getMentionSavingQuota().pipe(filter(x => !!x)),
                this.topicsService.getFocusedTopic().pipe(filter(x => !!x)),
                this.mentionSavingUsageDashboardService.getTopicsUsage(this.SAMPLE_RANGE).pipe(filter(x => !!x))
            ]).subscribe(([quotaInfo, search, facetResults]) => {
                this.loading = false;

                this.quotaInfo = quotaInfo;

                const savingSearch = facetResults?.find(result => String(result.field) === String(NeoFacetTermFieldApiEnum.SAVING_SEARCH));
                this.topicUsage = savingSearch?.values?.find(item => item.value === String(search.id))?.count ?? 0;

                const saveSourceType = facetResults?.find(result => String(result.field) === String(NeoFacetTermFieldApiEnum.SAVE_SOURCE_TYPE));
                this.overallUsage = saveSourceType?.values?.reduce((total, sourceType) => total += sourceType.count, 0) ?? 0;

                if (this.quotaInfo) {
                    this.generateEstimateRange();
                    this.generateEstimateChart();
                }
            }),
        );
    }

    private roundToNearestPowerOf10(num: number, roundUp: boolean): number {
        let numLength = Math.floor(Math.log10(num));
        numLength = numLength === 0 ? 1 : numLength;

        const powerOf10 = Math.pow(10, numLength);
        let roundedNum = Math.floor((num + (powerOf10 - 1)) / powerOf10) * powerOf10;

        if (roundUp) {
            return roundedNum;
        }

        if (num % powerOf10 !== 0) {
            roundedNum -= powerOf10;
        }

        return roundedNum;
    }

    generateEstimateRange(): void {
        const daysInMonth = moment.utc().daysInMonth();
        const topicUsageEstimate = this.topicUsage / this.SAMPLE_RANGE * daysInMonth;
        this.lowerBound = this.roundToNearestPowerOf10(topicUsageEstimate * (1 - this.RANGE_PERCENTAGE), false) || 0;
        this.upperBound = this.roundToNearestPowerOf10(topicUsageEstimate + (1 + this.RANGE_PERCENTAGE), true) || 0;
    }

    generateEstimateChart(): void {
        const {
            savingQuota,
            additionalMentionQuota,
            savingQuotaUsed
        } = this.quotaInfo;

        const daysInMonth = moment.utc().daysInMonth();
        const daysElapsedThisMonth = moment.utc().date();
        const remainDays = daysInMonth - daysElapsedThisMonth;
        const totalQuota = savingQuota + additionalMentionQuota;
        const overallUsageEstimate = savingQuotaUsed + this.overallUsage  / this.SAMPLE_RANGE * remainDays;
        const maxQuota = Math.max(totalQuota, overallUsageEstimate);

        const dataPoints = [];
        // Total Used
        const totalUsedProportion = Math.min(100, savingQuotaUsed / maxQuota * 100);
        dataPoints.push({
            from: 0,
            to: totalUsedProportion,
            name: this.localizationService.transform('Monitoring.mentionVolumeEstimateDialog.totalUsed'),
            fill: chartColors.agprMediumBlue,
            tickLength: 0
        });

        // total quota marker. This needs to be in the middle of the array so it does not affect corner radius in the chart
        if (totalQuota > 0) {
            dataPoints.push({
                to: (totalQuota / maxQuota) * 100,
                name: abbreviateNumber(totalQuota)
            });
        }

        // Overall Usage Estimate
        const topicUsageEstimate = savingQuotaUsed + (this.overallUsage - this.topicUsage) / this.SAMPLE_RANGE * remainDays;
        const overallUsageEstimateProportion = Math.min(100, topicUsageEstimate / maxQuota * 100);
        if (topicUsageEstimate) {
            dataPoints.push({
                from: totalUsedProportion,
                to: overallUsageEstimateProportion,
                name: this.localizationService.transform('Monitoring.mentionVolumeEstimateDialog.overallUsageEstimate'),
                fill: chartColors.agprLightBlue,
                tickLength: 0
            });
        }

        // Topic Usage Estimate
        const topicUsageEstimateProportion = Math.min(100, overallUsageEstimate / maxQuota * 100);
        if (this.topicUsage) {
            dataPoints.push({
                from: overallUsageEstimateProportion,
                to: topicUsageEstimateProportion,
                name: this.localizationService.transform('Monitoring.mentionVolumeEstimateDialog.topicUsageEstimate'),
                fill: chartColors.agprNegative,
                tickLength: 0
            });
        }

        // Remaining Quota
        if (overallUsageEstimate < maxQuota) {
            dataPoints.push({
                from: topicUsageEstimateProportion,
                to: 100,
                name: this.localizationService.transform('Monitoring.mentionVolumeEstimateDialog.remainingQuota'),
                fill: chartColors.agprNotAvailable,
                tickLength: 0
            });
        }

        const quotaEstimateSeries = {
            label: '',
            dataProvider: of(dataPoints)
        };

        const quotaEstimateChartStyles = new ChartStylesModel();
        quotaEstimateChartStyles.customPadding = {
            top: 35,
            right: 25,
            bottom: 5,
            left: 0
        };
        const estimateChartOptions: ProgressChartOptionsModel = {
            barHeight: 20,
            usesLegend: true,
            styles: quotaEstimateChartStyles
        };

        this.estimateChart = new ProgressChartModel([quotaEstimateSeries], estimateChartOptions);
    }

    openMentionSavingUsageDashboard(): void {
        this.coreDialogService.open(MentionSavingUsageDashboardComponent, {
            inputs: {
                overlayContainer: this.overlayContainer,
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                coverNav: true
            }
        });
    }

    close(): void {
        this.hideSlideOverlay = true;
    }

    handleVisibleChange(isVisible): void {
        if(!isVisible) {
            this.closeDialog();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
